import { AccommodationType, AmenityType, IAccommodationBlockParams } from "Bands/types";
import { copy } from "shared/assets";
import { Card, Title } from "shared/components";
import { GooglePlacesAddressForm } from "shared/components/Form/AddressForm/GooglePlacesAddressForm";
import { Spacer } from "shared/layout";
import { DateChangeProps, IAddress, ICardTheme, IFieldType, IFormFields } from "shared/types";
import { DateFormat, TimeFormat } from "shared/utils";

const { accommodationBlock: blockCopy } = copy;

export const getFormFields = ({
  handleStartDateChange,
  handleEndDateChange,
  isStartBeforeEnd,
  dateFormat,
  timeFormat,
  submitAddress,
  handleBlurAddress,
  defaultValues,
  address,
  setAddress,
  tempAddress,
  setTempAddress,
}: {
  address: IAddress | null;
  setAddress: (address: IAddress | null) => void;
  handleStartDateChange: (date: DateChangeProps) => void;
  handleEndDateChange: (date: DateChangeProps) => void;
  isStartBeforeEnd: boolean;
  dateFormat?: DateFormat;
  timeFormat?: TimeFormat;
  submitAddress?: (address: IAddress | null) => void;
  handleBlurAddress?: (address: IAddress | null) => void;
  defaultValues?: IAccommodationBlockParams;
  tempAddress: IAddress | null;
  setTempAddress: (address: IAddress | null) => void;
}): IFormFields => [
  {
    name: "address",
    component: (
      <Card theme={ICardTheme.secondary}>
        <Spacer size="m">
          <Title title={copy.venueProperties.address.title} />
          <GooglePlacesAddressForm
            address={address}
            setAddress={setAddress}
            tempAddress={tempAddress}
            setTempAddress={setTempAddress}
            defaultValues={defaultValues?.address ? defaultValues?.address : {}}
            submitAddress={submitAddress}
            blurAddress={handleBlurAddress}
          />
        </Spacer>
      </Card>
    ),
    style: {
      gridColumn: "span 4",
    },
  },
  {
    name: "accommodationType",
    label: blockCopy.create.fields.accommodationType,
    type: IFieldType.tagGroup,
    style: {
      gridColumn: "span 4",
    },
    isMultiple: false,
    options: Object.keys(AccommodationType).map(type => ({
      value: type,
      label: AccommodationType[type as keyof typeof AccommodationType],
    })),
  },
  {
    name: "bookingId",
    label: blockCopy.create.fields.bookingId,
    type: IFieldType.text,
    style: {
      gridColumn: "span 4",
    },
  },
  {
    name: "checkInDate",
    label: blockCopy.create.fields.checkInDate,
    type: IFieldType.datePicker,
    validation: {
      required: copy.errors.required,
      validate: () => isStartBeforeEnd || blockCopy.create.errors.startBeforeEnd,
    },
    onChange: (value: string) => handleStartDateChange({ date: value }),
    dateFormat,
    style: {
      gridColumn: "span 1",
    },
  },
  {
    name: "checkInTime",
    label: blockCopy.create.fields.checkInTime,
    type: IFieldType.simpleTime,
    validation: {
      validate: () => isStartBeforeEnd,
    },
    onChange: (value: string) => handleStartDateChange({ time: value }),
    timeFormat,
    style: {
      gridColumn: "span 1",
    },
  },
  {
    name: "checkOutDate",
    label: blockCopy.create.fields.checkOutDate,
    type: IFieldType.datePicker,
    validation: {
      required: copy.errors.required,
      validate: () => isStartBeforeEnd || blockCopy.create.errors.startBeforeEnd,
    },
    onChange: (value: string) => handleEndDateChange({ date: value }),
    dateFormat,
    style: {
      gridColumn: "span 1",
    },
  },
  {
    name: "checkOutTime",
    label: blockCopy.create.fields.checkOutTime,
    type: IFieldType.simpleTime,
    validation: {
      validate: () => isStartBeforeEnd,
    },
    onChange: (value: string) => handleEndDateChange({ time: value }),
    timeFormat,
    style: {
      gridColumn: "span 1",
    },
  },
  {
    name: "amenities",
    label: blockCopy.create.fields.amenities,
    type: IFieldType.tagGroup,
    style: {
      gridColumn: "span 3",
    },
    isMultiple: true,
    options: Object.keys(AmenityType).map(type => ({
      value: type,
      label: AmenityType[type as keyof typeof AmenityType],
    })),
  },
];
